import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { styled } from '@mui/material';

import { useAppSelector } from '@/state/store';
import { Button, Container, Header, PasswordField, Messages } from '@/components';
import { authentication } from '@/actions';
import t from '@/utils/translate';
import Banner from '@/components/Banner';

const Main = styled('main')(`
  position: fixed;
  top: 80px;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 24px;
`);

const Form = styled('form')(`
  margin: auto;
  width: 30%;
  min-width: 200px;
  max-width: 420px;
`);

export default () => {
  const passwordResetClaimId = useAppSelector((state) => state.authentication.passwordResetClaimId);
  const { code } = useParams();

  const [done, setDone] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean | undefined>(undefined);

  const nav = useNavigate();

  const scheduleRedirect = () => {
    window.setTimeout(() => {
      nav('/');
    }, 5000);
  };

  useEffect(() => {
    if (code) {
      authentication.checkPasswordResetClaim(code)
        .then(() => {
          setVerified(true);
        })
        .catch((err) => {
          setVerified(false);
          scheduleRedirect();
        });
    }
  }, [code]);

  const formik = useFormik({
    initialValues: { password: '' },
    validateOnBlur: false,
    validateOnChange: false,
    validate: ({ password }) => {
      const errors: Record<string, string> = {};

      if (!password || password.length < 6) {
        errors.password = t('pwd.validation.pwdLen');
      } else if (!/\d/.test(password)) {
        errors.password = t('pwd.validation.pwdDig');
      } else if (!/[a-zа-я]/.test(password) || !/[A-ZА-Я]/.test(password)) {
        errors.password = t('pwd.validation.pwdReg');
      }

      return errors;
    },
    onSubmit: async ({ password }) => {
      try {
        formik.setSubmitting(true);
        await authentication.resetPassword({
          ...(passwordResetClaimId && { claim: passwordResetClaimId }),
          password,
          code: code as string,
        });
        setDone(true);
      } finally {
        formik.setSubmitting(false);
        scheduleRedirect();
      }
    }
  });

  if (!code) {
    return (<Navigate to="/" />);
  }

  return (
    <>
      <Header/>

      <Main>
        {done && (
          <Banner>{`${t('pwdReset.message.done')}${!passwordResetClaimId ? t(` ${t('pwdReset.hint.usePwd')}`) : ''}`}</Banner>
        )}
        {!done && (
          <Form onSubmit={formik.handleSubmit}>
            <h3>{t('pwdReset.hint.enterNewPwd', true)}</h3>

            <Container margin-v='small'>
              <PasswordField
                {...formik.getFieldProps('password')}
                autoComplete="new-password"
                disabled={!verified || formik.isSubmitting}
                label={t('pwdReset.label.newPwd')}
                error={formik.touched.password && !!formik.errors.password}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                autoFocus
              />
            </Container>
            <Button
              disabled={!verified || formik.isSubmitting}
              type="submit"
              variant="contained"
              fullWidth
              color="primary"
              size="large"
            >
              {t('action.done', true)}
            </Button>
          </Form>
        )}

        <footer>
          <Messages />
        </footer>
      </Main>
    </>
  );
};